import { useApolloClient } from '@apollo/client';
import { useQueryClient } from '@tanstack/react-query';
import { isArray } from 'lodash-es';
import { useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';

import { ReactQueryKeys } from '../../../common/react-query-keys';
import { SwrCacheKeys } from '../../../common/swr-cache-keys';
import { getSessionCompanyId } from '../../../selectors/session';

export default function useUpdateFPACache() {
  const { mutate, cache } = useSWRConfig();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const companyId = useSelector(getSessionCompanyId);

  const updateFPACache = async ({ jobId }: { jobId?: string }) => {
    function refetchApplicationList() {
      mutate(
        key =>
          isArray(key) &&
          String(key[0]).startsWith(SwrCacheKeys.jobApplicationsShortKey)
      );
    }
    function refetchApplicationDetails() {
      const searchParams = new URLSearchParams(location.search);
      const isApplicationDetailsPage = new RegExp(
        '^(/jobs/).*(/applications).*',
        'i'
      ).test(location.pathname);

      apolloClient.refetchQueries({
        include: ['getApplicationById', 'getContactPacksInfo'],
      });
      queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.applicationDetailsShortKey,
        refetchType: 'active',
      });
      const isApplicationDetailsPopupOpen = Boolean(
        searchParams.get('viewApplicationDetailId')
      );
      // only active queries will be mutated
      mutate(key =>
        String(key).startsWith(SwrCacheKeys.applicationDetailsShortKey)
      );
      if (isApplicationDetailsPopupOpen || isApplicationDetailsPage) return;

      // below logic is present to prevent the application details popup from showing
      // VIP paywalls while the application is been retched
      const applicationCacheToEvict = [];
      for (const key of cache.keys()) {
        if (!key.startsWith(SwrCacheKeys.applicationDetailsShortKey)) continue;
        applicationCacheToEvict.push(() => cache.delete(key));
      }
      for (const evictFunc of applicationCacheToEvict) {
        evictFunc();
      }
    }

    setTimeout(() => {
      void queryClient.refetchQueries({
        queryKey: ReactQueryKeys.featureCount(companyId),
        type: 'all',
      });
      if (jobId) {
        void queryClient.refetchQueries({
          queryKey: ReactQueryKeys.jobFeatureCount(companyId, jobId),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.featureCountHotJobs(companyId),
      });
    }, 1000);

    refetchApplicationDetails();
    refetchApplicationList();
  };

  return updateFPACache;
}
